import { Box, ITheme, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { Utilities } from '../../utility/utils';
import { useTranslation } from 'react-i18next';
import { PortfolioFundAllocation } from '../../models/Portfolio';
import { LeoImagesFundsPath } from '../../resources/api-constants';

export interface IFundAllocationGraphProps {
    allocations: PortfolioFundAllocation[];
    bigFormat?: boolean;
}

const FundAllocationGraph: React.FC<IFundAllocationGraphProps> = ({ allocations, bigFormat }) => {
    const [selectedZone, setSelectedZone] = useState(0);
    const theme = useTheme() as ITheme;
    const { t, i18n } = useTranslation();
    const localAlloc = [...allocations];
    localAlloc.sort((a, b) => b.amount - a.amount);
    const totalFunds = allocations ? allocations.reduce((sum, current) => sum + current.amount, 0) : 0;
    const onSelectZone = (index: number) => {
        setSelectedZone(index);
    }
    if (allocations.length === 0) {
        return <></>;
    }
    const width = bigFormat ? 300 : 150
    const height = bigFormat ? 250 : 140
    return <Box display={"flex"} flexDirection={bigFormat ? "column" : "row"} alignItems={"center"}>
        <Box width={`${width}px`}>
            <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" xmlns="http://www.w3.org/2000/svg">
                <g transform={`translate(${width / 2},${height / 2})`}>
                    {localAlloc.map((allocation, index, array) => {
                        const proportion = allocation.amount / totalFunds;
                        const proportionRot = 2 * Math.PI * (proportion === 1 ? 0.9999 : proportion);
                        const totalDone = array.slice(0, index).reduce((sum, current) => sum + current.amount, 0);
                        const startAngle = 2 * Math.PI * totalDone / totalFunds - Math.PI / 4;
                        const size = bigFormat ? 80 : 50// selectedZone === index ? 65 : 50;
                        const textCoord = Utilities.f_svg_rotate_coord([30, 0], startAngle + proportionRot / 2);
                        const color = allocation.id === 0 ? theme.palette.leo.type[400] : (theme.palette.leo as any)[`color${index * 3 % 19 + 1}`]
                        return <g transform={`scale(${selectedZone === index ? 1.4 : 1})`} key={`fundgraph-${allocation.name}`} >
                            <path d={Utilities.f_svg_ellipse_arc([0, 0], [size, size], [0, proportionRot], startAngle, true)} fill={color} strokeWidth="8" onMouseEnter={() => onSelectZone(index)} />
                            {(proportion > 0.1 || selectedZone === index) && <foreignObject x={textCoord[0] - 15} y={textCoord[1] - 12.5} width={30} height={25} onMouseEnter={() => onSelectZone(index)}>
                                <Box width={"100%"} height={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                    <Typography fontSize={"11px"} lineHeight={"11px"} fontWeight={selectedZone === index ? 600 : 500} color={theme.palette.common.white} sx={{ cursor: "", userSelect: "none" }}>{Math.floor(proportion * 100)}%</Typography>
                                </Box>
                            </foreignObject>}
                            {/* <text x={textCoord[0]-20} y={textCoord[1]-20} fontSize={12} fill='black' width={40} height={40}>{Math.floor(proportion * 100)}%</text> */}
                        </g>
                    })}
                </g>
            </svg>
        </Box>
        {bigFormat ?
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"end"} flexGrow={1} gap={"8px"} width={"100%"}>
                <Box display={"flex"} flexDirection={"column"} gap={"23px"}>
                    {localAlloc[selectedZone].logo && <img src={`${LeoImagesFundsPath}/${localAlloc[selectedZone].logo}`} style={{ maxWidth: "90%", maxHeight: "25px", objectFit: "contain" }} />}
                    <Typography fontWeight={"700"} fontSize={"20px"} lineHeight={"24.38px"} color={theme.palette.leo.type[400]}>{localAlloc[selectedZone].name}</Typography>
                </Box>
                <Box display={"flex"} flexDirection={"column"} alignItems={"end"} gap={"10px"}>
                    <Typography fontWeight={"600"} fontSize={"20px"} lineHeight={"24.38px"} color={theme.palette.leo.type[400]}>{Math.floor(localAlloc[selectedZone].amount * 100 / totalFunds)}%</Typography>
                    <Typography fontWeight={"600"} fontSize={"20px"} lineHeight={"24.38px"} color={theme.palette.leo.type[400]}>€{localAlloc[selectedZone].amount.toLocaleString(i18n.language)}</Typography>
                </Box>
            </Box>
            :
            <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} flexGrow={1} gap={"8px"}>
                <img src={`${LeoImagesFundsPath}/${localAlloc[selectedZone].logo}`} style={{ maxWidth: "90%", maxHeight: "25px" }} alt={`${t('A11yFundLogoAlt')} ${localAlloc[selectedZone].name}`} />
                <Typography fontWeight={"700"} fontSize={"14px"} lineHeight={"17.07px"} color={theme.palette.leo.type[400]}>{localAlloc[selectedZone].name}</Typography>
                <Typography fontWeight={"400"} fontSize={"14px"} lineHeight={"17.07px"} color={theme.palette.leo.type[400]}>{Math.floor(localAlloc[selectedZone].amount * 100 / totalFunds)}% - €{localAlloc[selectedZone].amount.toLocaleString(i18n.language)}</Typography>
            </Box>
        }
    </Box>;
}

export default FundAllocationGraph;